import { getValidAccessToken } from './getValidAccessToken';
import { normalizeEnvKey } from './envKeyUtils';
import { decodeJwtParts } from './debug/jwtDebug';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getFunctionsUrl = () => {
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
  if (!supabaseUrl) throw new Error('Missing REACT_APP_SUPABASE_URL');
  return `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/game-round`;
};

const buildAuthHeaders = async () => {
  const headers = { 'Content-Type': 'application/json' };

  const jwtAnonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_JWT_ANON_KEY);
  const anonRaw = normalizeEnvKey(process.env.REACT_APP_SUPABASE_ANON_KEY);

  const jwtAnonDecoded = decodeJwtParts(jwtAnonRaw);
  const jwtAnonIsUsableJwt =
    jwtAnonDecoded?.partsCount === 3 &&
    !jwtAnonDecoded?.decodeError &&
    jwtAnonDecoded?.payload;

  const anonDecoded = decodeJwtParts(anonRaw);
  const anonLooksJwt =
    anonDecoded?.partsCount === 3 &&
    !anonDecoded?.decodeError &&
    anonDecoded?.payload;

  const apikeyToSend = jwtAnonIsUsableJwt
    ? jwtAnonRaw
    : anonLooksJwt
      ? anonRaw
      : null;

  const accessToken = await getValidAccessToken();
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  if (apikeyToSend) headers.apikey = apikeyToSend;

  return headers;
};

const postJson = async (body) => {
  const headers = await buildAuthHeaders();
  if (!headers.Authorization) return null; // not signed in — silently skip

  const response = await fetch(getFunctionsUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  // On 401, try once with a refreshed token
  if (response.status === 401) {
    const refreshed = await getValidAccessToken({ forceRefresh: true });
    if (refreshed) {
      const retryHeaders = { ...headers, Authorization: `Bearer ${refreshed}` };
      const retryRes = await fetch(getFunctionsUrl(), {
        method: 'POST',
        headers: retryHeaders,
        body: JSON.stringify(body),
      });
      return retryRes;
    }
  }

  return response;
};

const normalizeEarthGuessAverages = (raw) => {
  if (!raw || typeof raw !== 'object') return null;

  const parseEntry = (entry) => {
    if (!entry || typeof entry !== 'object') {
      return { avgGuessPct: null, gamesPlayed: 0 };
    }

    const avgGuessPctRaw = Number(entry.avg_guess_pct);
    const gamesPlayedRaw = Number(entry.games_played);

    return {
      avgGuessPct: Number.isFinite(avgGuessPctRaw) ? avgGuessPctRaw : null,
      gamesPlayed: Number.isFinite(gamesPlayedRaw) && gamesPlayedRaw > 0
        ? Math.floor(gamesPlayedRaw)
        : 0,
    };
  };

  return {
    earth_impossible: parseEntry(raw.earth_impossible),
    earth_impossible_ish: parseEntry(raw.earth_impossible_ish),
  };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Start a new game round (fire-and-forget safe).
 * @param {{ gameType: string }} params
 * @returns {Promise<string|null>} roundId or null on failure
 */
export const startRound = async ({ gameType }) => {
  try {
    const res = await postJson({ action: 'start', game_type: gameType });
    if (!res || !res.ok) return null;
    const data = await res.json();
    return data?.roundId || null;
  } catch {
    return null;
  }
};

/**
 * Finish a game round (fire-and-forget safe).
 * @param {{ roundId: string, won: boolean, guessesUsed: number, activeTimeMs: number, metadata?: object }} params
 * @returns {Promise<{ ok: boolean, durationMs?: number, activeTimeMs?: number } | null>}
 */
export const finishRound = async ({ roundId, won, guessesUsed, activeTimeMs, metadata }) => {
  if (!roundId) return null;
  try {
    const res = await postJson({
      action: 'finish',
      round_id: roundId,
      won,
      guesses_used: guessesUsed,
      active_time_ms: activeTimeMs,
      metadata: metadata || {},
    });
    if (!res || !res.ok) return null;
    const data = await res.json();
    return {
      ok: Boolean(data?.ok),
      durationMs: data?.duration_ms ?? null,
      activeTimeMs: data?.active_time_ms ?? null,
      pointsAwarded: data?.points_awarded ?? 0,
      firstGameBonus: Boolean(data?.first_game_bonus),
      coinsAwarded: data?.coins_awarded ?? 0,
      dailyCoinsRemaining: data?.daily_coins_remaining ?? null,
      earthGuessAverages: normalizeEarthGuessAverages(data?.earth_guess_averages),
    };
  } catch {
    return null;
  }
};

/**
 * Daily check-in (fire-and-forget safe).
 * Returns checkin status + season points data, or null on failure.
 */
export const checkin = async () => {
  try {
    const res = await postJson({ action: 'checkin' });
    if (!res || !res.ok) return null;
    const data = await res.json();
    return {
      ok: Boolean(data?.ok),
      alreadyCheckedIn: Boolean(data?.already_checked_in),
      pointsAwarded: data?.points_awarded ?? 0,
      checkinStreak: data?.checkin_streak ?? 0,
      streakMilestone: data?.streak_milestone ?? null,
      seasonPoints: data?.season_points ?? 0,
      lifetimePoints: data?.lifetime_points ?? 0,
      seasonId: data?.season_id ?? 0,
      previousSeason: data?.previous_season ?? null,
      referralPayout: data?.referral_payout ?? null,
    };
  } catch {
    return null;
  }
};
